// Copyright (c) 2019-present Ithpower, Inc. All Rights Reserved.
// See LICENSE.txt for license information.
import React from 'react';
import * as dd from 'dingtalk-jsapi';
import {message} from 'antd';
import Cookies from 'js-cookie';

import synchronizingImage from 'images/login/synchronizing.gif';
import {loadDdJsConfig, loadDingTalkEnterpriseId, loadDingtalkSyncStatus, loginByDdOauth} from 'server/actions/users';
import {loadEnterpriseAdmins} from 'server/actions/staffs';
import {IsvType} from 'types/users';
import GlobalContext from 'context/global_context';

import {setClientSingalByCookies} from 'utils/utils';
import {setDingtalkTitle} from 'utils/dingtalk_utils';
import {OrganizationSyncStatus} from 'utils/constants/general';
import {logs} from 'server/actions/general';

import TripartiteAuthBase, {AuthBaseProps} from './tripartite_auth_base';

const cookieNameToken = 'IAIAUTHTOKEN';

class DingtalkSSO extends TripartiteAuthBase {
    constructor(props: AuthBaseProps) {
        super(props);

        const search = new URLSearchParams(props.location.search);
        this.corpId = search.get('corpId');
        this.isvAppType = search.get('isvAppType') as IsvType;
        this.authToken = Cookies.get(cookieNameToken);
    }

    componentDidMount() {
        if (!this.corpId || !this.isvAppType) {
            this.handleAuthInvalid();
            return;
        }

        const title = this.isvAppType === 'Performance' ? '智办事绩效-登录' : '互成协作-登录';
        setDingtalkTitle(title);

        if (this.authToken) {
            this.verifyEnterpriseInfo();
        } else {
            this.checkOrganizationSyncStatus();
        }
    }

    reTry = () => {
        this.handleStatusInit();
        this.checkOrganizationSyncStatus();
    };

    checkOrganizationSyncStatus = () => {
        if (this.corpId) {
            loadDingtalkSyncStatus(this.corpId).then(({data}) => {
                if (data) {
                    if (data.status === OrganizationSyncStatus.COMPLETED) {
                        this.ssoLogin();
                    } else if (data.status === OrganizationSyncStatus.SYNC) {
                        this.handleSynchronizing();
                        if (this.syncTimer) {
                            clearTimeout(this.syncTimer);
                        }
                        this.syncTimer = setTimeout(this.checkOrganizationSyncStatus, 5000);
                    }
                }
            });
        }
    };

    ssoLogin = () => {
        dd.ready(() => {
            if (this.corpId) {
                dd.runtime.permission.requestAuthCode({
                    corpId: this.corpId, // 企业id
                }).then(({code}) => {
                    if (this.corpId && this.isvAppType) {
                        loginByDdOauth({
                            corp_id: this.corpId,
                            auth_code: code,
                            isv_app_type: this.isvAppType,
                        }).then(({error}) => {
                            if (error) {
                                if (error.server_error_id === 'no_ding_talk_auth') {
                                    const appName = this.isvAppType === 'Performance' ? '智办事绩效' : '互成协作';
                                    this.settingErrorMessage(`你还没有开通应用的使用权限，请联系企业管理员在“工作台-全员-${appName}-可见范围”帮你开通。`);
                                    this.handleAuthError();
                                } else {
                                    this.handleAuthError();
                                }
                                return;
                            }
                            this.finishSignin();
                        });
                    }
                }, () => {
                    this.handleAuthError();
                }).catch(() => {
                    //  如不显式调用catch，promise会报错
                });
            }
        });
    };

    finishSignin = () => {
        setClientSingalByCookies();
        this.verifyEnterpriseInfo();
    };

    verifyEnterpriseInfo = async () => {
        // 登录完成后SESSION_COOKIE_ISVAPPTYPE会被后端写入
        // 此处做钉钉config假调用
        const url = window.location.href;

        if (!this.corpId || !this.isvAppType) {
            return;
        }
        await loadDdJsConfig(this.corpId, url, this.isvAppType).then(({data, error}) => {
            if (error) {
                return false;
            }
            dd.config({
                ...data,
                jsApiList: [
                    'device.base.getUUID',
                    'biz.util.open',
                ], // 必填，需要使用的jsapi列表，注意：不要带dd。
            });
            return true;
        }).then((success) => {
            if (success) {
                dd.ready(() => {
                    dd.device.base.getUUID({
                        onSuccess: (res: any) => {
                            // 调用成功时回调
                            // eslint-disable-next-line no-console
                            console.log(res);
                        },
                        onFail: (err: any) => {
                            // 调用失败时回调
                            // eslint-disable-next-line no-console
                            console.log(err);
                        },
                        // eslint-disable-next-line no-empty-function
                    }).catch(() => {
                        //  如不显式调用catch，promise会报错
                    });
                });
            }
        });

        let enterpriseId: string | undefined;
        if (typeof document !== 'undefined' && typeof document.cookie !== 'undefined') {
            const cookies = document.cookie.split(';');
            for (let i = 0; i < cookies.length; i++) {
                const cookie = cookies[i].trim();
                if (cookie.startsWith('IAIENTERPRISEID=')) {
                    enterpriseId = cookie.replace('IAIENTERPRISEID=', '');
                    break;
                }
            }
        }
        if (!enterpriseId || !this.corpId) {
            message.error('登录失败，请尝试重新登录');
            return;
        }
        const checkEnterprise = await loadDingTalkEnterpriseId(this.corpId).then(({error, data}) => {
            if (error) {
                if (error.server_error_id === 'invalid_corp_id') {
                    // 企业id不存在，统一认为是服务端正在同步架构
                    return 'sync';
                }
                return 'error';
            } else if (data && data.enterprise_id === enterpriseId) {
                return 'correct';
            }
            return 'imparity';
        });
        if (checkEnterprise === 'correct') {
            loadEnterpriseAdmins().then(({error, data}) => {
                if (error) {
                    setTimeout(this.verifyEnterpriseInfo, 5000);
                } else if (data && data.length !== 0) {
                    this.redirectToHomePage();
                } else {
                    const backUrl = encodeURI(window.location.href);
                    window.location.replace(`/backend/admin_setting?from=${backUrl}`);
                }
            });
        } else if (checkEnterprise === 'imparity') {
            this.ssoLogin();
        } else if (checkEnterprise === 'sync') {
            this.checkOrganizationSyncStatus();
        } else {
            message.error('登录失败，请尝试重新登录');
        }
    };

    renderSynchronizing = () => {
        return (
            <div className={'tripartite-app-placeholder-wrapper'}>
                <img
                    className='loading-image'
                    src={synchronizingImage}
                    alt='loading'
                />
                <div className={'tripartite-app-placeholder-text'}>
                    <div className={'tripartite-app-placeholder-title'}>
                        {'稍等一下，正在为你同步钉钉通讯录'}
                    </div>
                    <div className={'tripartite-app-placeholder-tip'}>
                        {'同步过程预计需要1-2分钟...'}
                    </div>
                </div>
            </div>
        );
    };
}

class WrapperContext extends React.PureComponent<{location: any}> {
    render() {
        return (
            <GlobalContext.Consumer>
                {({clientConfig}) =>
                    (<DingtalkSSO
                        location={this.props.location}
                        clientConfig={clientConfig}
                    />)
                }
            </GlobalContext.Consumer>
        );
    }
}

export default WrapperContext;
